import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

// Toast Context
const ToastContext = createContext(null);

// Toast types and their styles
const TOAST_TYPES = {
  success: {
    icon: 'CheckCircle',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    iconColor: 'text-green-600',
    titleColor: 'text-green-900',
    messageColor: 'text-green-700',
    progressColor: 'bg-green-500',
  },
  error: {
    icon: 'XCircle',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-600',
    titleColor: 'text-red-900',
    messageColor: 'text-red-700',
    progressColor: 'bg-red-500',
  },
  warning: {
    icon: 'AlertTriangle',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-900',
    messageColor: 'text-amber-700',
    progressColor: 'bg-amber-500',
  },
  info: {
    icon: 'Info',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-900',
    messageColor: 'text-blue-700',
    progressColor: 'bg-blue-500',
  },
};

// Default durations (ms)
const DEFAULT_DURATION = 5000;
const DURATIONS = {
  success: 3000,
  error: 6000,
  warning: 5000,
  info: 4000,
};

// Toast Item Component
const ToastItem = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const styles = TOAST_TYPES[toast.type] || TOAST_TYPES.info;
  const duration = toast.duration || DURATIONS[toast.type] || DEFAULT_DURATION;

  useEffect(() => {
    // Progress bar animation
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 50);

    // Auto dismiss
    const timeout = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300);
  };

  return (
    <div
      className={cn(
        "w-full max-w-sm rounded-lg border shadow-lg overflow-hidden transition-all duration-300",
        styles.bgColor,
        styles.borderColor,
        isExiting ? "opacity-0 translate-x-full" : "opacity-100 translate-x-0"
      )}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          {/* Icon */}
          <div className={cn("flex-shrink-0", styles.iconColor)}>
            <Icon name={styles.icon} size={20} />
          </div>

          {/* Content */}
          <div className="ml-3 flex-1">
            {toast.title && (
              <h4 className={cn("text-sm font-semibold", styles.titleColor)}>
                {toast.title}
              </h4>
            )}
            {toast.message && (
              <p className={cn("text-sm mt-0.5", styles.messageColor)}>
                {toast.message}
              </p>
            )}
            {toast.action && (
              <div className="mt-2">
                <button
                  onClick={() => {
                    toast.action.onClick();
                    handleDismiss();
                  }}
                  className={cn(
                    "text-sm font-medium underline hover:no-underline",
                    styles.iconColor
                  )}
                >
                  {toast.action.label}
                </button>
              </div>
            )}
          </div>

          {/* Dismiss Button */}
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleDismiss}
              className={cn(
                "inline-flex rounded-md p-1 hover:bg-black/5 transition-colors",
                styles.iconColor
              )}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 w-full bg-black/5">
        <div
          className={cn("h-full transition-all duration-50 ease-linear", styles.progressColor)}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, onDismiss }) => {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((options) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const toast = {
      id,
      type: 'info',
      ...options,
    };
    
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const toast = useCallback((message, options = {}) => {
    return addToast({ message, ...options });
  }, [addToast]);

  const success = useCallback((message, options = {}) => {
    return addToast({ type: 'success', message, ...options });
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    return addToast({ type: 'error', message, ...options });
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    return addToast({ type: 'warning', message, ...options });
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    return addToast({ type: 'info', message, ...options });
  }, [addToast]);

  // Promise-based toast (for async operations)
  const promise = useCallback((promiseToResolve, { loading, success, error }) => {
    const loadingId = addToast({
      type: 'info',
      message: loading || 'Loading...',
      duration: Infinity, // Don't auto-dismiss
    });

    promiseToResolve
      .then((result) => {
        dismissToast(loadingId);
        addToast({
          type: 'success',
          message: typeof success === 'function' ? success(result) : success,
        });
        return result;
      })
      .catch((err) => {
        dismissToast(loadingId);
        addToast({
          type: 'error',
          message: typeof error === 'function' ? error(err) : error,
        });
      });

    return promiseToResolve;
  }, [addToast, dismissToast]);

  const value = {
    toasts,
    toast,
    success,
    error,
    warning,
    info,
    promise,
    dismiss: dismissToast,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Default export for simple usage
export default ToastProvider;











